import { useEffect } from 'react';
import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface FeaturesPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

function FeatureIcon({ paths, size = 32 }: { paths: string[]; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M2 7l3.5 3.5L12 3" />
    </svg>
  );
}

const FEATURES = [
  {
    num: '01',
    title: 'Reads the full conversation thread',
    body: 'Salifly reads your complete conversation, every message not just the most recent, so it has full context on the history and arc of the deal.',
    details: ['Gmail threads, LinkedIn DMs, Outlook web, Salesforce Chatter', 'Salesforce Inbox, HubSpot CRM notes, Outreach sequences', 'Any platform with a conversation thread in the browser', 'Graceful fallback when content is restricted or protected'],
    paths: ['M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'],
  },
  {
    num: '02',
    title: 'Hybrid Context Engine: dual payload, zero blind spots',
    body: 'Salifly sends two simultaneous inputs to defeat AI blindness. The screenshot captures spatial context — lead identity confirmed from page headers and avatars, CRM layout, and UI anchors. The DOM-scraped thread provides the authoritative text source — every message, objection, and buying signal. Cross-referencing both sources produces richer, higher-confidence deal reads than either input alone.',
    details: ['Screenshot → spatial layout, lead identity, CRM UI anchors', 'DOM thread → authoritative message content, deal state, objections, buying signals', 'Cross-source correlation drives the "high" confidence classification', 'Confidence indicator surfaces in the UI so you know when to verify manually'],
    paths: ['M15 12a3 3 0 11-6 0 3 3 0 016 0z', 'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'],
  },
  {
    num: '03',
    title: 'Three ready-to-send responses',
    body: 'Every analysis produces three distinct responses, each calibrated to the detected deal stage, industry playbook, and friction points from the actual conversation. You pick the one that feels right and inject it with one click.',
    details: ['Each response adapts to the detected deal stage and lead language', 'Tone, length, and goal are all adjustable before you generate', 'Refine any response further with a single instruction', 'One-click injection into Gmail, LinkedIn, and CRM compose fields'],
    paths: ['M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z'],
  },
  {
    num: '04',
    title: 'Universal — any platform, any industry, zero config',
    body: 'Salifly is not built for one CRM or one vertical. It auto-detects the platform from the DOM and adapts tone calibration for email, DM, or CRM note automatically. Sales reps in SaaS, real estate, healthcare, finance, automotive, and professional services all use the same extension — context detection and playbook selection happen silently behind the scenes.',
    details: ['Gmail, Outlook Web App, LinkedIn, LinkedIn Sales Navigator', 'Salesforce, HubSpot, Outreach, Gong Engage, and 10+ more', 'Industry-aware playbooks: SaaS, real estate, healthcare, finance, automotive, services', 'Platform auto-detection: tone calibrated for email vs. DM vs. CRM note'],
    paths: ['M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z'],
  },
  {
    num: '05',
    title: '2-Layer Privacy Shield',
    body: 'Two independent privacy layers protect your conversation text before it reaches any AI model. Layer 1 strips PII from the thread via client-side regex before it leaves your browser. Layer 2 re-runs the same patterns server-side inside each edge function before any AI processing call — defense in depth against client-side misses. Screenshots are sent at full resolution to our secure AI servers for maximum visual accuracy.',
    details: ['Layer 1 — Client regex: emails, phones, SSNs, FICO labels, 16-digit card numbers, account IDs, addresses', 'Layer 2 — Server regex: same patterns re-applied inside edge functions before any AI processing call', 'Full-resolution screenshots sent directly to secure AI servers for maximum accuracy', 'AI API keys managed as edge function secrets — never stored in the extension or sent to the client'],
    paths: ['M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'],
  },
  {
    num: '06',
    title: 'Inline phrase suggestions',
    body: 'Salifly underlines weak or generic phrases in your reply with a purple dotted underline. Click to see a replacement and the reason behind it, one phrase at a time.',
    details: ['Detects hedging language, filler phrases, and jargon', 'Suggestions surfaced inline without interrupting your flow', 'Consolidated side panel shows all suggestions at a glance', 'Works in Gmail compose, LinkedIn reply, and Salesforce fields'],
    paths: ['M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z'],
  },
  {
    num: '07',
    title: 'Auto-learning knowledge base',
    body: 'On paid plans, Salifly learns from every reply you send. It quietly extracts the durable facts about your business — pricing, plan tiers, hours, financing, warranty terms — and builds a private knowledge base that makes every future draft more accurate and more you. The more you sell, the smarter it gets.',
    details: ['Learns durable business facts from each sent reply, automatically', 'Private per-rep knowledge base — never shared and never used to train any model', 'Future drafts draw on what Salifly already knows about your business', 'Unlimited on every paid plan — Basic, Pro, and Team'],
    paths: ['M9 18h6M10 21h4M12 3a6 6 0 00-3.6 10.8c.5.4.9 1 1 1.7l.1.5h5l.1-.5c.1-.7.5-1.3 1-1.7A6 6 0 0012 3z'],
  },
  {
    num: '08',
    title: 'Follow-up radar',
    body: 'Salifly tracks the last interaction timestamp for every lead and surfaces conversations that have gone quiet for 3 or more days. Leads are ordered by deal-state urgency — a stalling Negotiation ranks above a cold Discovery — so the highest-risk threads always appear first. The extension icon badge shows a live count so you catch what is slipping without switching context.',
    details: ['Stale leads surface on the welcome screen sorted by deal-state urgency', 'Extension icon shows a live badge count of stale conversations', 'Click any entry to jump straight back into that lead\'s thread', 'Badge clears automatically once you re-analyze the lead'],
    paths: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2'],
  },
  {
    num: '09',
    title: 'Win-rate play badges',
    body: 'Every draft card shows your personal reply rate for that exact play angle — "★ 62% replied for you." Once you have sent at least three messages with a given play label, Salifly surfaces the rate on the card and marks your top performer with a star. Below that data floor the badge shows a neutral "picked N×" count instead — it never shows a rate it cannot stand behind.',
    details: ['Tracks reply rate per play label — Pattern Interrupt, ROI Anchor, Challenger Ask', 'Star marks the highest reply-rate play in each generated set', 'Minimum 3 sends before any reply rate is displayed', 'Drawn from your own history only — never against other reps\' data'],
    paths: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  },
];

export function FeaturesPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: FeaturesPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-wrap">
      <Nav currentPage="features" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      {/* Hero */}
      <div className="page-hero">
        <span className="page-hero-eyebrow">All nine features</span>
        <h1 className="page-hero-h1">
          Everything you need<br />to <em>reach your goals</em>
        </h1>
        <p className="page-hero-sub">
          Built on three technical moats: a Hybrid Context Engine that defeats AI blindness, a 2-Layer Privacy Shield that masks data before it moves, and a universal platform adapter that works on any tab, in any industry, with zero configuration.
        </p>
        <div className="page-hero-cta">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('how-it-works')}>How it works</button>
        </div>
      </div>

      {/* Feature list */}
      <div className="page-section" style={{ paddingTop: 20 }}>
        <div className="fp-feature-list">
          {FEATURES.map((f) => (
            <div key={f.num} className={`fp-feature-item reveal`}>
              <div className="fp-feature-copy">
                <span className="fp-feature-num">{f.num}</span>
                <h3 className="fp-feature-h3">{f.title}</h3>
                <p className="fp-feature-body">{f.body}</p>
                <ul style={{ listStyle: 'none', margin: '20px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {f.details.map(d => (
                    <li key={d} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, font: '400 14px/1.55 var(--font-sans)', color: 'var(--fg-2)' }}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }}><CheckIcon /></span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="fp-feature-visual">
                {f.num === '06' ? (
                  <div className="fp-inline-mockup">
                    <div className="fp-inline-compose-bar">
                      <span className="fp-inline-dot" />
                      <span className="fp-inline-dot" />
                      <span className="fp-inline-dot" />
                      <span className="fp-inline-compose-lbl">Gmail · Compose</span>
                    </div>
                    <p className="fp-inline-text">
                      I just wanted to{' '}
                      <span className="fp-sug-mark">touch base</span>
                      {' '}and{' '}
                      <span className="fp-sug-mark">circle back</span>
                      {' '}before we{' '}
                      <span className="fp-sug-mark">move forward</span>.
                    </p>
                    <div className="fp-inline-badge">
                      <span className="fp-inline-badge-num">3</span>
                      suggestions
                    </div>
                    <div className="fp-inline-tip">
                      <div className="fp-inline-tip-row">
                        <span className="fp-inline-tip-cat">Tone</span>
                        <span className="fp-inline-tip-original">"I just wanted to touch base and circle back on our previous conversation before we move forward."</span>
                      </div>
                      <div className="fp-inline-tip-replace">→ I am circling back on our discussion before moving forward."</div>
                      <p className="fp-inline-tip-reason">Filler phrase. Specific timing drives action.</p>
                    </div>
                  </div>
                ) : (
                  <div className="fp-feature-icon-large">
                    <FeatureIcon paths={f.paths} size={44} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="page-footer-cta">
        <h2>Ready for better outcomes?</h2>
        <p>Start free. No credit card required. Install in 30 seconds.</p>
        <div className="page-footer-cta-actions">
          <button className="page-btn-primary" onClick={onGetStarted}>Get started free →</button>
          <button className="page-btn-ghost" onClick={() => onNavigate('pricing')}>View pricing</button>
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <a href="/privacy" className="page-footer-link">Privacy</a>
              <a href="/terms" className="page-footer-link">Terms</a>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
